import { useState } from 'react';
import { Botao, type TamanhoBotao } from './ui';
import { aba, baixarXlsx, type AbaPronta, type ColunaDaPlanilha } from '../servicos/planilha';

/**
 * O botão "Extrair xlsx" das listas.
 *
 * Leva a lista inteira da tela, não o que está digitado na busca — a mesma
 * regra das exportações da carteira: uma planilha que muda conforme o filtro
 * do momento ninguém consegue reproduzir depois. Lista paginada no servidor
 * passa `carregar`, que busca todas as páginas antes de montar a planilha.
 *
 * O erro aparece embaixo do botão: no cabeçalho da página não há outro lugar,
 * e um download que falha calado parece que não foi clicado.
 */
export function BotaoExtrairXlsx<T>(
  props: {
    readonly titulo: string;
    readonly disabled?: boolean;
    /** O dos botões ao lado — no cabeçalho do Pipeline eles são pequenos. */
    readonly tamanho?: TamanhoBotao;
  } & (
    | {
        readonly colunas: readonly ColunaDaPlanilha<T>[];
        readonly linhas: readonly T[];
        readonly carregar?: never;
        readonly abas?: never;
      }
    | {
        readonly colunas: readonly ColunaDaPlanilha<T>[];
        readonly carregar: () => Promise<readonly T[]>;
        readonly linhas?: never;
        readonly abas?: never;
      }
    | {
        /** Mais de uma tabela (um relatório): cada uma vira uma aba. */
        readonly abas: () => readonly AbaPronta[];
        readonly colunas?: never;
        readonly linhas?: never;
        readonly carregar?: never;
      }
  ),
): JSX.Element {
  const [extraindo, setExtraindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const extrair = async (): Promise<void> => {
    setExtraindo(true);
    setErro(null);
    try {
      const abas =
        props.abas !== undefined
          ? props.abas()
          : [aba(props.titulo, props.colunas, props.linhas ?? (await props.carregar()))];
      await baixarXlsx(props.titulo, abas);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível extrair a planilha.');
    } finally {
      setExtraindo(false);
    }
  };

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <Botao
        variante="secundario"
        tamanho={props.tamanho ?? 'medio'}
        carregando={extraindo}
        disabled={props.disabled === true}
        title="Baixa a lista inteira em planilha do Excel"
        onClick={() => void extrair()}
      >
        Extrair xlsx
      </Botao>
      {erro !== null && (
        <span role="alert" className="max-w-xs text-right text-xs text-erro">
          {erro}
        </span>
      )}
    </span>
  );
}
